import * as React from "react";
import { Loader, Skeleton } from "@kuda/ui";

export default function Loading({
  open,
  type = "backdrop",
  variant = "table",
  rows = 16,
  columns = 5,
  height = "75vh",
}) {
  // Full-screen overlay loader
  if (type === "backdrop") {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-color-bg-overlay-60">
        <Loader type="white" />
      </div>
    );
  }

  // Skeleton loading variants
  if (type === "skeleton" && open) {
    switch (variant) {
      case "table":
        return (
          <div className="w-full p-4 flex flex-col items-center justify-center" style={{ height }}>
            <div className="w-full">
              <div className="flex flex-col gap-3 mb-4 items-center">
                <Skeleton className="w-full rounded-lg" style={{ height: 30 }} />
                <Skeleton className="w-full rounded-lg" style={{ height: 30 }} />
              </div>
              {/* Table header skeleton */}
              <div className="flex flex-row gap-4 mb-4 justify-center">
                {Array.from(new Array(columns)).map((_, colIndex) => (
                  <Skeleton key={colIndex} className="rounded" style={{ height: 40, flex: 1 }} />
                ))}
              </div>

              {/* Table rows skeleton */}
              {Array.from(new Array(rows)).map((_, rowIndex) => (
                <div key={rowIndex} className="flex flex-row gap-4 mb-3 justify-center">
                  {Array.from(new Array(columns)).map((_, colIndex) => (
                    <Skeleton key={colIndex} className="rounded" style={{ height: 52, flex: 1 }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        );

      case "card":
        return (
          <div className="w-full p-4">
            {Array.from(new Array(rows)).map((_, index) => (
              <div key={index} className="mb-6 p-4 border border-color-border-secondary rounded-lg">
                <div className="flex flex-col gap-2">
                  <Skeleton className="rounded" style={{ width: "60%", height: 32 }} />
                  <Skeleton className="rounded" style={{ width: "40%", height: 24 }} />
                  <Skeleton className="rounded" style={{ width: "100%", height: 60 }} />
                  <div className="flex flex-row gap-2">
                    <Skeleton className="rounded" style={{ width: 80, height: 32 }} />
                    <Skeleton className="rounded" style={{ width: 80, height: 32 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "form":
        return (
          <div className="w-full mx-auto p-4" style={{ maxWidth: 600 }}>
            <div className="flex flex-col gap-6">
              <Skeleton className="rounded" style={{ width: "50%", height: 40 }} />
              <Skeleton className="rounded" style={{ width: "100%", height: 56 }} />
              <Skeleton className="rounded" style={{ width: "100%", height: 56 }} />
              <Skeleton className="rounded" style={{ width: "70%", height: 56 }} />
              <Skeleton className="rounded" style={{ width: "100%", height: 120 }} />
              <div className="flex flex-row gap-4 mt-2">
                <Skeleton className="rounded" style={{ width: 100, height: 40 }} />
                <Skeleton className="rounded" style={{ width: 100, height: 40 }} />
              </div>
            </div>
          </div>
        );

      case "list":
        return (
          <div className="w-full p-4">
            {Array.from(new Array(rows)).map((_, index) => (
              <div key={index} className="flex flex-row gap-4 mb-4 items-center">
                <Skeleton className="rounded-full" style={{ width: 40, height: 40 }} />
                <div className="flex-1">
                  <Skeleton className="rounded" style={{ width: "80%", height: 20 }} />
                  <Skeleton className="rounded mt-2" style={{ width: "60%", height: 16 }} />
                </div>
                <Skeleton className="rounded" style={{ width: 80, height: 32 }} />
              </div>
            ))}
          </div>
        );

      case "code":
        return (
          <div className="p-4 mt-5" style={{ width: "58%" }}>
            <div className="flex flex-col gap-2 mb-4 items-center">
              <Skeleton className="w-full rounded-lg" style={{ height: 30 }} />
              <Skeleton className="w-full rounded-lg" style={{ height: 30 }} />
            </div>
            <div className="flex flex-col gap-2 mb-4 items-center">
              <Skeleton className="rounded-lg" style={{ width: "50%", height: 30 }} />
            </div>
            <div className="rounded-lg p-4 bg-color-bg-tertiary overflow-hidden" style={{ height: "60vh" }}>
              <div className="flex flex-col gap-2">
                {Array.from(new Array(rows)).map((_, index) => (
                  <Skeleton
                    key={index}
                    className="rounded"
                    style={{ width: `${80 - (index % 5) * 10}%`, height: 20 }}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="w-full p-4">
            <Skeleton className="w-full rounded" style={{ height }} />
          </div>
        );
    }
  }

  // Return nothing if not open
  return null;
}
